import {
    ExpenseAdvanceStatus,
    ExpenseClaimDTO,
    ExpenseClaimStatus,
    ExpensePolicyDTO,
} from '../interfaces/expense.interface';

export const CLAIM_STATUS_LABEL: Record<ExpenseClaimStatus, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Pending approval',
    IN_REVIEW: 'In review',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    PAID: 'Paid',
    CANCELLED: 'Cancelled',
};

export const CLAIM_STATUS_COLOR: Record<ExpenseClaimStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
    DRAFT: 'default',
    SUBMITTED: 'info',
    IN_REVIEW: 'warning',
    APPROVED: 'primary',
    REJECTED: 'error',
    PAID: 'success',
    CANCELLED: 'default',
};

export const ADVANCE_STATUS_LABEL: Record<ExpenseAdvanceStatus, string> = {
    REQUESTED: 'Pending approval',
    APPROVED: 'Approved',
    PAID: 'Paid',
    SETTLED: 'Settled',
    CANCELLED: 'Cancelled',
};

export const claimStatusLabel = (status: ExpenseClaimStatus): string => CLAIM_STATUS_LABEL[status] ?? status;
export const advanceStatusLabel = (status: ExpenseAdvanceStatus): string => ADVANCE_STATUS_LABEL[status] ?? status;

/**
 * Claims carry no server-issued number, so show a short, stable, readable reference derived from
 * the id (first 8 hex chars, upper-cased) -- never the raw UUID slice with an ellipsis.
 */
export const shortRef = (prefix: string, id: string): string => `${prefix}-${id.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
export const claimRef = (id: string): string => shortRef('EXP', id);
export const advanceRef = (id: string): string => shortRef('ADV', id);

/** Thousands-separated, always 2 decimals. */
export const formatAmount = (value: number | string | null | undefined): string => {
    if (value == null || value === '') return '—';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** True when the claim's current tier is the last one its amount requires (approval is then final). */
export const isFinalApprovalTier = (policy: ExpensePolicyDTO | null, claim: ExpenseClaimDTO): boolean => {
    if (!policy || policy.tiers.length === 0) return true;
    const tier = claim.currentApprovalTier ?? 1;
    const matchingTier = policy.tiers.find(t =>
        claim.totalAmount >= t.minAmount && (t.maxAmount == null || claim.totalAmount < t.maxAmount),
    );
    if (!matchingTier) return true;
    return tier >= matchingTier.sequence;
};
